import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SkeletonCardProps {
  className?: string;
  variant?: 'project' | 'insight' | 'default';
}

export function SkeletonCard({ className, variant = 'default' }: SkeletonCardProps) {
  if (variant === 'project') {
    return (
      <Card className={cn('border-l-4 border-l-muted', className)}>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            {/* Logo skeleton */}
            <Skeleton className="w-14 h-14 rounded-lg flex-shrink-0" />
            
            <div className="flex-1 min-w-0 space-y-3">
              {/* Header */}
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              
              {/* Grid */}
              <div className="grid grid-cols-2 gap-2.5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="p-2.5 bg-muted/30 rounded-md border border-border/50 min-h-[60px]">
                    <Skeleton className="h-3 w-12 mb-2" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
              
              {/* Footer */}
              <div className="flex items-center justify-between pt-2.5 border-t border-border/50">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-8 w-24 rounded-md" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'insight') {
    return (
      <Card className={cn('border-border/50', className)}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <Skeleton className="h-8 w-8 rounded" />
            <Skeleton className="h-8 w-12" />
          </div>
          <Skeleton className="h-5 w-24 mb-1" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </CardContent>
    </Card>
  );
}

export function SkeletonProjectList({ count = 3 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} variant="project" />
      ))}
    </div>
  );
}

export function SkeletonInsightCards({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} variant="insight" />
      ))}
    </div>
  );
}
